/**
 * Injects GA4 (gtag.js), Hotjar, and Google Search Console verification
 * based on admin-configured IDs from /api/settings/public.
 *
 * Also fires GA4 page_view events on every React-Router navigation so SPA
 * routes are tracked correctly.
 */
import { useEffect, useRef } from "react";
import { useLocation } from "react-router-dom";
import { publicSettings } from "@/lib/api";

const ATTR = "data-rp-analytics";

function injectScript({ src, inner, async = true, defer = false }) {
  if (typeof document === "undefined") return null;
  const s = document.createElement("script");
  s.setAttribute(ATTR, "1");
  if (src) s.src = src;
  if (async) s.async = true;
  if (defer) s.defer = true;
  if (inner) s.text = inner;
  document.head.appendChild(s);
  return s;
}

function injectMeta(name, content) {
  if (typeof document === "undefined" || !content) return null;
  let tag = document.querySelector(`meta[name="${name}"]`);
  if (!tag) {
    tag = document.createElement("meta");
    tag.setAttribute("name", name);
    tag.setAttribute(ATTR, "1");
    document.head.appendChild(tag);
  }
  tag.setAttribute("content", content);
  return tag;
}

let _gaInjected = false;
let _hjInjected = false;
let _gaId = "";

function setupGA4(ga4Id) {
  if (_gaInjected || !ga4Id) return;
  _gaInjected = true;
  _gaId = ga4Id;
  injectScript({ src: `https://www.googletagmanager.com/gtag/js?id=${ga4Id}` });
  injectScript({
    inner: `window.dataLayer = window.dataLayer || [];
function gtag(){dataLayer.push(arguments);}
window.gtag = gtag;
gtag('js', new Date());
gtag('config', '${ga4Id}', { send_page_view: false });`,
  });
}

function setupHotjar(hjid) {
  if (_hjInjected || !hjid) return;
  _hjInjected = true;
  injectScript({
    inner: `(function(h,o,t,j,a,r){
  h.hj=h.hj||function(){(h.hj.q=h.hj.q||[]).push(arguments)};
  h._hjSettings={hjid:${Number(hjid)},hjsv:6};
  a=o.getElementsByTagName('head')[0];
  r=o.createElement('script');r.async=1;r.setAttribute('${ATTR}','1');
  r.src=t+h._hjSettings.hjid+j+h._hjSettings.hjsv;
  a.appendChild(r);
})(window,document,'https://static.hotjar.com/c/hotjar-','.js?sv=');`,
  });
}

export default function useAnalytics() {
  const location = useLocation();
  const inited = useRef(false);

  // Pull IDs from /api/settings/public once on mount.
  useEffect(() => {
    publicSettings
      .get()
      .then((s) => {
        const a = s?.analytics || {};
        if (a.gsc_verification) injectMeta("google-site-verification", a.gsc_verification);
        if (a.ga4_id) setupGA4(a.ga4_id);
        if (a.hotjar_id) setupHotjar(a.hotjar_id);
        inited.current = true;
      })
      .catch(() => {});
  }, []);

  // Fire a GA4 page_view on every React-Router navigation.
  useEffect(() => {
    if (!window.gtag || !_gaId) return;
    window.gtag("event", "page_view", {
      page_path: location.pathname + location.search,
      page_location: window.location.href,
      page_title: document.title,
      send_to: _gaId,
    });
  }, [location.pathname, location.search]);
}
